import { Dialog } from '@base-ui/react/dialog';
import { dismissedAnnouncementsAtom } from '@web-app/atoms/dismissedAnnouncementsAtom';
import { APPOINTMENT_ROUTE } from '@web-app/components/consts';
import type { BellPinnedSection } from '@web-app/components/Shell/shell-contribution';
import { Button } from '@web-app/components/ui/Button/button';
import { useAnnouncements } from '@web-app/hooks/queries/announcements';
import {
  useMarkNotificationViewed,
  useNotifications,
} from '@web-app/hooks/queries/notifications';
import { cn } from '@web-app/lib/utils';
import { format, parseISO } from 'date-fns';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { LuBell } from 'react-icons/lu';

interface PanelItem {
  id: string;
  title: string;
  /** ISO date string. Announcements may omit it; notifications always have one. */
  date?: string | null;
  onClick: () => void;
}

const itemClass = cn(
  'border-b border-border px-4 py-2 text-left',
  'hover:bg-primary-100',
  'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset',
);

export const BellIndicator = ({
  pinned,
}: {
  /** Needs action: keeps the dot on until its items are gone, not just seen. */
  pinned?: BellPinnedSection | null;
}) => {
  const router = useRouter();
  const { data: notifications } = useNotifications();
  const { data: announcements } = useAnnouncements();
  const { mutate: markAsViewed } = useMarkNotificationViewed();
  const setDismissedIds = useSetAtom(dismissedAnnouncementsAtom);

  const [isOpen, setIsOpen] = useState(false);
  const [bellSeen, setBellSeen] = useState(false);

  const items: PanelItem[] = [
    ...(announcements ?? []).map((announcement) => ({
      id: announcement.id,
      title: announcement.title,
      date: announcement.publishedAt,
      onClick: () => {
        setDismissedIds((prev) => prev.filter((id) => id !== announcement.id));
        setIsOpen(false);
        router.push(APPOINTMENT_ROUTE);
      },
    })),
    ...(notifications ?? []).map((notification) => ({
      id: notification.id,
      title: notification.title,
      date: notification.createdAt,
      onClick: () => {
        markAsViewed(notification.id);
        setIsOpen(false);
        router.push(notification.route);
      },
    })),
  ];

  // An empty pinned section renders nothing, like no section at all.
  const section = pinned?.items.length ? pinned : null;
  const footer = section?.footer;
  if (items.length === 0 && !section) {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) setBellSeen(true);
  };

  return (
    <div className="relative inline-block">
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
        <Dialog.Trigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Notifications"
              className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring"
            >
              <LuBell size={16} />
            </Button>
          }
        />

        <Dialog.Portal>
          <Dialog.Popup className="fixed left-[10px] top-[80px] z-10 w-[350px] rounded-[10px] border border-border bg-card text-card-foreground shadow-default-md outline-none">
            <Dialog.Title className="border-b border-border px-4 py-3 text-base font-semibold">
              Notification Center
            </Dialog.Title>
            <div
              aria-live="polite"
              className="flex max-h-[400px] flex-col overflow-y-auto pb-2"
            >
              {section && (
                <section aria-label={section.title}>
                  <div className="flex items-center justify-between gap-2 border-b border-border bg-muted px-4 py-2 text-xs font-semibold tracking-wide text-primary uppercase">
                    <span className="flex items-center gap-2">
                      {section.icon}
                      {section.title}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {section.countLabel}
                    </span>
                  </div>
                  {section.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setIsOpen(false);
                        item.onClick();
                      }}
                      className={cn(
                        itemClass,
                        'flex w-full gap-2 bg-destructive/5',
                      )}
                    >
                      <span
                        aria-hidden
                        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-destructive"
                      />
                      <span className="min-w-0">
                        <span className="line-clamp-2 block text-sm font-medium">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {item.meta}
                        </span>
                      </span>
                    </button>
                  ))}
                  {items.length > 0 && (
                    <div className="border-b border-border bg-muted px-4 py-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      Portal
                    </div>
                  )}
                </section>
              )}
              {items.map((item, index) => {
                const isLast = index === items.length - 1;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={item.onClick}
                    className={cn(itemClass, isLast && 'border-b-0')}
                  >
                    <p className="line-clamp-2 text-sm font-medium">
                      {item.title}
                    </p>
                    {item.date && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {format(parseISO(item.date), 'MMM d, yyyy')}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
            {footer && (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  footer.onClick();
                }}
                className="w-full border-t border-border px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-primary-100"
              >
                {footer.label}
              </button>
            )}
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>

      {(section || !bellSeen) && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute right-1 top-1 size-2 rounded-full bg-destructive',
            section && 'animate-pulse motion-reduce:animate-none',
          )}
        />
      )}
    </div>
  );
};
